import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  Activity,
  Briefcase,
  Building2,
  CheckSquare,
  Home,
  ListTodo,
  MoreHorizontal,
  Plus,
  Send,
  Settings,
  Users,
} from "lucide-react";
import { useTranslate } from "ra-core";
import { Link, matchPath, useLocation, useMatch, useNavigate } from "react-router";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { useState } from "react";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/offer/*", location.pathname)) {
    currentPath = "/offer";
  } else if (matchPath("/channels/*", location.pathname)) {
    currentPath = "/channels";
  } else if (matchPath("/approval_queue/*", location.pathname)) {
    currentPath = "/approval_queue";
  } else if (matchPath("/agent_activity/*", location.pathname)) {
    currentPath = "/agent_activity";
  } else {
    currentPath = false;
  }

  // Check if the app is running as a PWA (standalone mode)
  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  // Check if it's iOS on the web
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);

  const moreIsActive =
    currentPath === "/companies" ||
    currentPath === "/deals" ||
    currentPath === "/offer" ||
    currentPath === "/channels" ||
    currentPath === "/approval_queue" ||
    currentPath === "/agent_activity" ||
    !!matchPath("/settings", location.pathname);

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 bg-secondary h-14"
      style={{
        paddingBottom: isPwa && isWebiOS ? 15 : undefined,
        height:
          "calc(var(--spacing)) * 6" + (isPwa && isWebiOS ? " + 15px" : ""),
      }}
    >
      <div className="flex justify-center">
        <NavigationButton
          href="/"
          Icon={Home}
          label={translate("ra.page.dashboard")}
          isActive={currentPath === "/"}
        />
        <NavigationButton
          href="/contacts"
          Icon={Users}
          label={translate("resources.contacts.name", { smart_count: 2 })}
          isActive={currentPath === "/contacts"}
        />
        <CreateButton />
        <NavigationButton
          href="/tasks"
          Icon={ListTodo}
          label={translate("resources.tasks.name", { smart_count: 2 })}
          isActive={currentPath === "/tasks"}
        />
        <MoreButton isActive={moreIsActive} />
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      "flex-col gap-1 h-auto py-2 px-1 rounded-md w-16",
      isActive ? null : "text-muted-foreground",
    )}
  >
    <Link to={href}>
      <Icon className="size-6" />
      <span className="text-[0.6rem] font-medium">{label}</span>
    </Link>
  </Button>
);

const CreateButton = () => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);

  return (
    <>
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            size="icon"
            className="h-16 w-16 rounded-full -mt-3"
            aria-label={translate("ra.action.create")}
          >
            <Plus className="size-10" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => setContactCreateOpen(true)}
          >
            {translate("resources.contacts.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => setNoteCreateOpen(true)}
          >
            {translate("resources.notes.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => setTaskCreateOpen(true)}
          >
            {translate("resources.tasks.forcedCaseName")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

const MoreButton = ({ isActive }: { isActive: boolean }) => {
  const translate = useTranslate();
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "flex-col gap-1 h-auto py-2 px-1 rounded-md w-16",
            isActive ? null : "text-muted-foreground",
          )}
          aria-label={translate("crm.navigation.more", { _: "More" })}
        >
          <MoreHorizontal className="size-6" />
          <span className="text-[0.6rem] font-medium">
            {translate("crm.navigation.more", { _: "More" })}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="end" className="mb-1 min-w-44">
        <DropdownMenuItem
          className="h-12 px-4 text-base gap-3"
          onSelect={() => navigate("/companies")}
        >
          <Building2 className="size-5 text-muted-foreground" />
          {translate("resources.companies.name", { smart_count: 2 })}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="h-12 px-4 text-base gap-3"
          onSelect={() => navigate("/deals")}
        >
          <Briefcase className="size-5 text-muted-foreground" />
          {translate("resources.deals.name", { smart_count: 2 })}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="h-12 px-4 text-base gap-3"
          onSelect={() => navigate("/offer")}
        >
          <Send className="size-5 text-muted-foreground" />
          {translate("resources.offer.name", { smart_count: 2, _: "Offer" })}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="h-12 px-4 text-base gap-3"
          onSelect={() => navigate("/channels")}
        >
          <Send className="size-5 text-muted-foreground" />
          {translate("resources.channels.name", {
            smart_count: 2,
            _: "Channels",
          })}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="h-12 px-4 text-base gap-3"
          onSelect={() => navigate("/approval_queue")}
        >
          <CheckSquare className="size-5 text-muted-foreground" />
          {translate("resources.approval_queue.name", {
            smart_count: 2,
            _: "Approvals",
          })}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="h-12 px-4 text-base gap-3"
          onSelect={() => navigate("/agent_activity")}
        >
          <Activity className="size-5 text-muted-foreground" />
          {translate("resources.agent_activity.name", {
            smart_count: 2,
            _: "Agent Activity",
          })}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="h-12 px-4 text-base gap-3"
          onSelect={() => navigate("/settings")}
        >
          <Settings className="size-5 text-muted-foreground" />
          {translate("crm.settings.title")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
